/* eslint-disable no-nested-ternary */
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Zoom from '@mui/material/Zoom';
import { PageHeader } from '@qpr/components';
import { Property } from '@qpr/components/Properties/views/properties-view-model';
import PropertiesView from '@qpr/components/Properties/views/PropertiesView';
import VerticalScroll from '@qpr/components/ScrollBar/VerticalScroll';
import React, { useEffect } from 'react';
import ConnectionsIcon from '../../../assets/ConnectionsIcon';
import { useFilter } from '../../Filter/components/FilterContext';
import Search from '../../Filter/components/Search';
import FilterChipsView from '../../Filter/views/FilterChipsView';
import FilterListView from '../../Filter/views/FilterListView';
import { Hierarchy } from '../map-state';
import Attributes from './Attributes';
import { useExpand } from './ExpandContext';
import GroupContainer from './GroupContainer';
import MapArea from './MapArea';
import MapItemContainer from './MapItemContainer';
const PREFIX = 'Map';

const classes = {
  disableBorder: `${PREFIX}-disableBorder`,
  pageHeader: `${PREFIX}-pageHeader`,
  filterChips: `${PREFIX}-filterChips`,
  buttonGroup: `${PREFIX}-buttonGroup`,
  icon: `${PREFIX}-icon`,
  scrollContainer: `${PREFIX}-scrollContainer`,
  scrollContent: `${PREFIX}-scrollContent`,
  errorText: `${PREFIX}-errorText`
};

interface MapProps {
  hierarchy: Hierarchy;
  attributeInfo: Property[];
}

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.disableBorder}`]: {
    borderRight: 'none!important'
  },

  [`& .${classes.pageHeader}`]: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
    color: 'white',
    flexBasis: '30px',
    overflow: 'visible'
  },

  [`& .${classes.filterChips}`]: {
    alignItems: 'center',
    overflow: 'visible'
  },

  [`& .${classes.buttonGroup}`]: { float: 'right', whiteSpace: 'nowrap', color: 'white' },

  [`& .${classes.icon}`]: {
    marginRight: theme.spacing(1)
  },

  [`& .${classes.scrollContainer}`]: {
    borderRadius: '4px',
    height: '100%'
  },

  [`& .${classes.scrollContent}`]: {
    paddingRight: theme.spacing(1),
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    }
  },

  [`& .${classes.errorText}`]: {
    color: theme.palette.getContrastText(theme.palette.text.primary),
    marginTop: '25vh'
  }
}));

interface MapProps {
  hierarchy: Hierarchy;
  attributeInfo: Property[];
}

const Map = ({ hierarchy, attributeInfo }: MapProps) => {
  const [selectedItem, setselectedItem] = React.useState<Hierarchy | undefined>(undefined);

  useEffect(() => {
    const initData = async () => {
      if (selectedItem) setselectedItem(undefined);
    };
    initData();
  }, [hierarchy]);

  const { expandAll, collapseAll, resetAll } = useExpand();

  const { getActiveFilters, getSearchFilter } = useFilter();

  const activeFilters = getActiveFilters().length > 0 || getSearchFilter() !== undefined;

  useEffect(() => (activeFilters ? expandAll() : resetAll()), [activeFilters]);

  const mapItems = (map: any) => (item: Hierarchy) => {
    return item.children?.length > 0 ? item.children.map(map) : null;
  };

  const getContentColumns = (contents: unknown[]) => {
    return (
      <>
        <Grid direction="column" style={{ padding: '0px 4px' }} container item xs={12} md={6}>
          <Grid item xs={12} sx={{ maxWidth: '100%!important' }}>
            {contents.filter((x: unknown, idx: number) => idx % 2 === 0)}
          </Grid>
        </Grid>
        <Grid direction="column" style={{ padding: '0px 4px' }} container item xs={12} md={6}>
          <Grid item xs={12} sx={{ maxWidth: '100%!important' }}>
            {contents.filter((x: unknown, idx: number) => idx % 2 !== 0)}
          </Grid>
        </Grid>
      </>
    );
  };

  const getContents = (hierarchyLevel: number) => (content: Hierarchy) => {
    return (
      <MapItemContainer
        key={content.id}
        title={content.name || `name missing (id: ${content.id})`}
        hierarchyLevel={hierarchyLevel}
        onPropertiesClick={() => setselectedItem(content)}
        subheader={<Attributes data={content.attributes} />}
      >
        {mapItems(getContents(hierarchyLevel + 1))(content)}
      </MapItemContainer>
    );
  };

  const getGroups = (group: Hierarchy) => {
    const contents = mapItems(getContents(0))(group);

    return (
      <Grid item xs={12}>
        <GroupContainer
          key={group.id}
          title={group.name || `name missing (id: ${group.id})`}
          onPropertiesClick={() => setselectedItem(group)}
          subheader={<Attributes data={group.attributes} />}
        >
          {contents ? getContentColumns(contents) : null}
        </GroupContainer>
      </Grid>
    );
  };

  const buildParents = (hierarchyToIterate: Hierarchy) => {
    if (hierarchyToIterate.children?.length > 0) {
      hierarchyToIterate.children.forEach((child: Hierarchy) => {
        child.parentPath = hierarchyToIterate.parentPath
          ? `${hierarchyToIterate.parentPath} / ${hierarchyToIterate.name}`
          : `${hierarchyToIterate.name}`;
        buildParents(child);
      });
    }
  };

  if (hierarchy.children?.length > 0) {
    hierarchy.children.forEach((child: Hierarchy) => {
      buildParents(child);
    });
  }

  if (hierarchy.id === '') return null;

  const renderError = (message: string) => (
    <Typography className={classes.errorText} align="center">
      {message}
    </Typography>
  );

  const renderMap = (items: Hierarchy) => (
    <MapArea>
      <VerticalScroll
        containerClassName={classes.scrollContainer}
        contentClassName={classes.scrollContent}
      >
        <Zoom unmountOnExit in={selectedItem !== undefined} timeout={{ enter: 300, exit: 250 }}>
          <div style={{ zIndex: 10, position: 'absolute', width: '100%', height: '100%' }}>
            <PropertiesView
              customProperties={attributeInfo}
              onClose={() => setselectedItem(undefined)}
              itemId={selectedItem?.fullid}
              path={selectedItem?.parentPath}
              name={selectedItem?.name}
            />
          </div>
        </Zoom>
        <Grid container spacing={1}>
          {mapItems(getGroups)(items)}
        </Grid>
      </VerticalScroll>
    </MapArea>
  );

  return (
    <Root
      sx={{
        paddingLeft: 1,
        paddingRight: 1,
        display: 'flex',
        flexFlow: 'column',
        height: 'calc(100vh - 64px)'
      }}
    >
      <div className={classes.pageHeader}>
        <PageHeader
          title={hierarchy.name || `name missing (id: ${hierarchy.id})`}
          icon={<ConnectionsIcon fontSize="inherit" />}
        />
        <ButtonGroup
          variant="text"
          aria-label="view controls"
          className={classes.buttonGroup}
          classes={{ groupedText: classes.disableBorder }}
          color="inherit"
          disabled={selectedItem !== undefined}
        >
          <Search />
          <FilterListView elementId={hierarchy.id} disabled={selectedItem !== undefined} />
          <Button
            sx={{ color: 'white' }}
            data-testid="expand-all-button"
            onClick={() => expandAll()}
          >
            <ExpandMore className={classes.icon} />
            EXPAND ALL
          </Button>
          <Button
            sx={{ color: 'white' }}
            data-testid="collapse-all-button"
            onClick={() => collapseAll()}
          >
            <ExpandLess className={classes.icon} />
            COLLAPSE ALL
          </Button>
        </ButtonGroup>
      </div>
      <div className={classes.filterChips}>
        <FilterChipsView />
      </div>
      {hierarchy.children?.length > 0
        ? renderMap(hierarchy)
        : activeFilters
        ? renderError('No contents matching filter criteria found.')
        : renderError('No data to map')}
    </Root>
  );
};

export default Map;
